import React, {
	useCallback,
	forwardRef,
	useState,
	useEffect,
	useRef,
	ButtonHTMLAttributes,
	FocusEventHandler,
	SyntheticEvent,
} from 'react';
import BaseButton from '../BaseButton';
import { BaseButtonProps } from '../BaseButton';
import { DirtyFormProxyButtonProps } from '../DirtyFormProxy/types';

const DELAYED_DISABLE_TIMEOUT = 1400;

export type ButtonProps = {
	type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
	onFocus?: FocusEventHandler;
	onBlur?: FocusEventHandler;
	onClick?: (e: SyntheticEvent) => void;
	disabled?: boolean;
	stopPropagation?: boolean;
	delayedDisable?: boolean;
	delayedDisableTimeOut?: number;
	children?: React.ReactNode;
} & Omit<BaseButtonProps, 'tagName'> &
	DirtyFormProxyButtonProps;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
	const {
		children,
		onClick,
		onFocus,
		onBlur,
		disabled,
		stopPropagation,
		type = 'button',
		delayedDisable = false,
		delayedDisableTimeOut = DELAYED_DISABLE_TIMEOUT,
	} = props;

	const delayedDisableTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [localDisabled, setLocalDisabled] = useState(delayedDisable ? false : disabled);
	const handleComponentClick = useCallback(
		(e) => {
			if (disabled) {
				return;
			}

			if (stopPropagation) {
				e.stopPropagation();
			}

			onClick && onClick(e);
		},
		[onClick, disabled, stopPropagation],
	);

	useEffect(() => {
		if (delayedDisable) {
			if (disabled) {
				delayedDisableTimer.current = setTimeout(() => {
					setLocalDisabled(true);
				}, delayedDisableTimeOut);
			} else {
				setLocalDisabled(false);
			}
		} else {
			setLocalDisabled(disabled);
		}

		return () => {
			if (delayedDisableTimer.current !== null) {
				clearTimeout(delayedDisableTimer.current);
			}
		};
	}, [disabled, delayedDisable, delayedDisableTimeOut]);

	const domProps = {
		onClick: handleComponentClick,
		onFocus,
		onBlur,
		type,
	};

	return (
		<BaseButton
			{...props}
			ref={ref}
			tagName='button'
			applyOnElementProps={domProps}
			disabled={localDisabled}
		>
			{children}
		</BaseButton>
	);
});

Button.displayName = 'Button';
